import React from 'react';
import { Container, SectionHeader, Card, Badge } from '../ui';
import { FEES, FEE_TIERS, PRICE_EXAMPLES } from '../../data/marketing';
import CostCalculator from './CostCalculator';

// Transparent Pricing section. All figures are clearly labeled SAMPLE / EXAMPLE
// pricing - not final, no competitor names, no "half the price" claims.
const Pricing = () => (
  <section id="pricing" className="bg-ink-50 py-16 sm:py-24 scroll-mt-16">
    <Container>
      <SectionHeader
        align="center"
        eyebrow="Pricing"
        title="Transparent pricing"
        subtitle="Honest, district-friendly pricing. Your district keeps 100% of ticket revenue — convenience and payment processing fees are paid by attendees."
      />

      {/* Value props */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {FEES.map((f) => (
          <Card key={f.title} className="p-5 flex gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-brand-50 flex items-center justify-center text-xl">{f.icon}</div>
            <div>
              <h3 className="text-base font-bold font-display text-ink-900">{f.title}</h3>
              <p className="mt-1 text-sm text-ink-500 leading-relaxed">{f.desc}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Sample tier table + example breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10 items-start">
        {/* Tier table */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold font-display text-ink-900">Sample convenience fees</h3>
            <Badge tone="warning">Example pricing</Badge>
          </div>
          <ul className="divide-y divide-ink-100">
            {FEE_TIERS.map((t) => (
              <li key={t.range} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-ink-600">{t.range}</span>
                <span className="font-semibold text-ink-900">{t.fee}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink-400 mt-4">
            Illustrative example only — not a binding offer or contract. Convenience fees are paid by attendees; final pricing is agreed with your district.
          </p>
        </Card>

        {/* Example cards */}
        <div className="space-y-4">
          {PRICE_EXAMPLES.map((ex) => (
            <Card key={ex.label} className="p-5">
              <div className="flex items-center justify-between">
                <p className="font-bold font-display text-ink-900">{ex.label}</p>
                <Badge tone="neutral">Example</Badge>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl bg-brand-50 p-3">
                  <p className="text-brand-700 font-medium">School receives</p>
                  <p className="text-xl font-bold font-display text-brand-800">{ex.face}</p>
                  <p className="text-xs text-ink-500">100% of ticket revenue</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <p className="text-ink-600 font-medium">Attendee pays</p>
                  <p className="text-sm text-ink-800 mt-0.5">
                    {ex.face} <span className="text-ink-400">+</span> {ex.fee} convenience fee <span className="text-ink-400">+</span> payment processing
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <CostCalculator />

      <p className="text-center text-xs text-ink-400 mt-8 max-w-2xl mx-auto">
        All figures are example, sample, and illustrative pricing only — not a binding offer or contract.
        Schools keep 100% of ticket revenue; convenience and payment processing fees are paid by attendees.
        No setup fees, no annual contracts, local support included. Your district's pricing is confirmed
        together during a demo.
      </p>
    </Container>
  </section>
);

export default Pricing;
